<template>
    <DeadlineReminderCard
        v-if="notification.type === 'Deadline Reminder'"
        :notification="notification"
        @open="redirectToLink(notification)"
        @toggle-read="toggleReadStatus(notification, !notification.read)"
    />
    <div v-else class="card-content is-relative pt-4 pb-4">
        <div v-if="!notification.read" class="red-dot" />

        <div class="is-flex is-align-center">
            <div class="media-left mr-0">
                <e-tooltip :label="notification.creator?.name" position="is-right" append-to-body>
                    <user-menu
                        :user="notification.creator"
                        :show-name="false"
                        :show-dropdown="false"
                        avatar-skeleton
                    />
                </e-tooltip>
            </div>
            <div class="media-content notification-content" @click="redirectToLink(notification)">
                <h3 class="notification-title">
                    <span class="title-text">{{ title }}</span>
                    <small class="notification-meta"> {{ courseCode }} - {{ createdAt }} </small>
                </h3>

                <p class="notification-body">
                    {{ body }}
                </p>
            </div>
            <div class="media-right is-flex is-flex-direction-column">
                <div class="is-flex is-flex-end">
                    <e-tooltip :label="notification.type" append-to-body position="is-left">
                        <e-icon
                            class="type-icon"
                            :icon="notification.drawer_styles.icon"
                            size="is-big"
                            type="is-primary"
                        />
                    </e-tooltip>
                </div>

                <div>
                    <e-tooltip
                        :label="`Mark as ${notification.read ? 'Unread' : 'Read'}`"
                        position="is-left"
                        class="mr-3"
                    >
                        <a
                            href="#"
                            aria-label="Toggle Read Status"
                            @click.prevent="toggleReadStatus(notification, !notification.read)"
                        >
                            <e-icon
                                class="mr-1"
                                :icon="notification.read ? 'circle' : 'check-circle'"
                                size="is-small"
                            />
                        </a>
                    </e-tooltip>

                    <e-tooltip label="Inbox" position="is-left">
                        <a
                            :href="notification.inbox_link"
                            aria-label="Inbox"
                            @click.prevent="redirectToLink(notification)"
                        >
                            <e-icon icon="arrow-circle-right" size="is-small" />
                        </a>
                    </e-tooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DeadlineReminderCard from '@/components/alerts/DeadlineReminderCard.vue';
import UserMenu from '@/components/user-menu/UserMenu.vue';
import { handleResponseError } from '@/Helpers.js';
import BellNotification from '@/models/BellNotification.js';

export default {
    name: 'AlertCard',
    components: { UserMenu, DeadlineReminderCard },
    emits: ['update-notification'],

    props: {
        notification: {
            type: Object,
            required: true,
        },
        isExpanded: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        title() {
            return this.notification.title ? this.truncate(this.notification.title, 3) : 'Untitled';
        },
        body() {
            return this.notification.body ? this.truncate(this.notification.body, 15) : 'No message.';
        },
        courseCode() {
            return this.notification.course_code ?? 'No course code';
        },
        createdAt() {
            return this.notification.formatted_created_at;
        },
    },

    methods: {
        async toggleReadStatus(notification, read) {
            if (notification.type === 'Deadline Reminder') {
                const pending = read ? notification.markAsRead() : notification.markAsUnread();
                this.$emit('update-notification', notification, { immediate: true, pending });
                try {
                    await pending;
                    await BellNotification.fetch();
                } catch (error) {
                    handleResponseError(error);
                }
                return;
            }

            await BellNotification.update({
                where: notification.notification_id,
                data: { read },
            });
            this.$emit('update-notification', notification);
        },

        async redirectToLink(notification) {
            if (!notification.inbox_link) {
                return;
            }

            // Mark as read before navigating so the unread count is correct when
            // the destination page loads and re-fetches it. We await the request
            // (rather than fire-and-forget) because the full-page navigation would
            // otherwise cancel the in-flight PUT. Navigate regardless of outcome.
            try {
                await notification.markAsRead();
            } catch {
                // Swallow — navigation should still proceed on a failed read.
            } finally {
                window.location.href = notification.inbox_link;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~assets/scss/toolbox.scss';

.card-content {
    border-bottom: 1px solid $grey-lighter;
}

.red-dot {
    top: 50%;
    left: 5px;
    width: 0.6rem;
    height: 0.6rem;
    border-radius: 50%;
    position: absolute;
    background-color: #ff5e73;
    transform: translateY(-50%);
}

// On mobile the card padding shrinks, so give the avatar room
// to the right of the unread dot to prevent overlap.
@include mobile {
    .media-left {
        margin-left: 1rem;
    }
}

.notification-title {
    line-height: 1.2;
    font-weight: bold;
    display: flex;
    align-items: baseline;
    flex-wrap: nowrap;
    column-gap: 0.5rem;
}
.title-text {
    font-weight: bold;
    // Allow the title to shrink and truncate so the meta stays on the same line.
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.notification-meta {
    margin-left: auto;
    // Never shrink or wrap the timestamp — the title gives way instead.
    flex: none;
    font-size: 0.75rem;
    font-weight: normal;
    color: $grey;
    white-space: nowrap;
}

.notification-body {
    margin-top: 1rem;
    font-size: 12px;
}
.notification-content {
    cursor: pointer;
    // Bulma's .media-content defaults to flex-shrink: 0, which lets the no-wrap
    // timestamp force this column wide and push the action buttons off-screen.
    // Allow it to shrink so the title ellipsizes and the actions stay visible.
    flex: 1 1 auto;
    min-width: 0;
}
.type-icon {
    font-size: 2rem;
}
</style>
